import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import type { LoginStatus } from '../../auth/domain/LoginStatus'
import type { Affiliate } from '../../shared/domain/Affiliate'
import { Market } from '../../shared/domain/Market'
import type { Devices } from '../../shared/domain/Device'
import { Banner } from './Banner'
import type { BannerSubtypes } from './BannerSubtype'
import type { BannerTypes } from './BannerType'
import type {
  GETBannerInformationResponseSafeType,
  GETBannerInformationResponseType,
} from '../infrastructure/HTTPMarketingRepository/schemas/banners'
import { BannerSchema } from '../infrastructure/HTTPMarketingRepository/schemas/banners'

export const ListOfBannerValidation = z.object({
  list: z.instanceof(Banner, { message: '[ListOfBanner] list is required' }).array(),
  market: z.instanceof(Market, { message: '[ListOfBanner] market is required' }),
})

export class ListOfBanner extends Model {
  static create({ list, market }: z.infer<typeof ListOfBannerValidation>) {
    ListOfBannerValidation.parse({ list, market })
    return new ListOfBanner(list, market)
  }

  static empty() {
    return new ListOfBanner([], Market.empty())
  }

  static fromApi({ list, market }: { list: GETBannerInformationResponseSafeType; market: Market }) {
    const validatedBanners = ListOfBanner.#validateApiPayload(list)

    const validBanners = validatedBanners
      .map(item => {
        try {
          return Banner.fromApiDataset({ info: item })
        } catch {
          return null
        }
      })
      .filter((banner): banner is Banner => banner !== null)

    return ListOfBanner.create({
      list: validBanners,
      market,
    })
  }

  static #validateApiPayload(
    list: GETBannerInformationResponseSafeType
  ): GETBannerInformationResponseType['banners'] {
    return list.reduce<GETBannerInformationResponseType['banners']>((acc, banner) => {
      const parsed = BannerSchema.safeParse(banner)

      if (parsed.success) {
        acc.push(parsed.data)
      }

      return acc
    }, [])
  }

  static fromJSON(json: ReturnType<ListOfBanner['toJSON']>) {
    return new ListOfBanner(
      json.list.map(item => Banner.fromJSON(item)),
      Market.fromJSON(json.market)
    )
  }

  static fromJSONOrEmpty(json: ReturnType<ListOfBanner['toJSON']>) {
    try {
      return ListOfBanner.fromJSON(json)
    } catch {
      return ListOfBanner.empty()
    }
  }

  constructor(
    private readonly _list: Banner[],
    private readonly _market: Market
  ) {
    super()
  }

  get list() { return this._list } // prettier-ignore

  get market() { return this._market } // prettier-ignore

  getBannerByType(type: BannerTypes) {
    return this._list.find(bannerInfo => bannerInfo.getType() === type)
  }

  getBannerBySubtype(subtype: BannerSubtypes) {
    return this._list.find(bannerInfo => bannerInfo.getSubtype() === subtype)
  }

  getBannerBySubtypeAndViewport(subtype: BannerSubtypes, viewport: Devices) {
    const specificBanner = this._list.find(
      bannerInfo => bannerInfo.getSubtype() === subtype && bannerInfo.viewport?.is(viewport)
    )

    if (specificBanner) {
      return specificBanner
    }

    return this._list.find(
      bannerInfo => bannerInfo.getSubtype() === subtype && !bannerInfo.viewport
    )
  }

  getBillboardBannerInformation({
    market,
    affiliate,
    loginStatus,
    device,
  }: {
    market?: Market
    affiliate?: Affiliate
    loginStatus?: LoginStatus
    device: Devices
  }): ReturnType<Banner['getBillboardBannerInformation']> {
    const campaignBanner = this.getBannerBySubtypeAndViewport('campaign', device)

    if (campaignBanner) {
      return campaignBanner.getBillboardBannerInformation()
    }

    if (
      device === 'mobile' &&
      !market?.isOneOf(['es', 'us']) &&
      loginStatus?.isUnlogged() &&
      affiliate?.isCug()
    ) {
      const memberBanner = this.getBannerBySubtypeAndViewport('member', device)

      return memberBanner?.getBillboardBannerInformation()
    }

    return this.getBannerBySubtypeAndViewport('brand', device)?.getBillboardBannerInformation()
  }

  getAnnouncementBannerInformation({
    device,
  }: {
    device: Devices
  }): ReturnType<Banner['getAnnouncementBannerInformation']> {
    const banner = this._list.find(
      bannerInfo =>
        bannerInfo.getType() === 'announcement-banner' &&
        (bannerInfo.viewport?.is(device) || !bannerInfo.viewport)
    )

    return banner?.getAnnouncementBannerInformation()
  }

  getBlockBannerInformation({
    device,
  }: {
    device: Devices
  }): ReturnType<Banner['getBlockBannerInformation']> {
    const banner = this._list.find(
      bannerInfo =>
        bannerInfo.getType() === 'block-banner' &&
        (bannerInfo.viewport?.is(device) || !bannerInfo.viewport)
    )

    return banner?.getBlockBannerInformation()
  }

  getHeroBannerInformation({
    device,
  }: {
    device: Devices
  }): ReturnType<Banner['getHeroBannerInformation']> {
    const banner = this._list.find(
      bannerInfo =>
        bannerInfo.getType() === 'hero-banner' &&
        (bannerInfo.viewport?.is(device) || !bannerInfo.viewport)
    )

    return banner?.getHeroBannerInformation()
  }

  toJSON() {
    return {
      list: this._list.map(item => item.toJSON()),
      market: this._market.toJSON(),
    }
  }
}
